import React, { useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, Outlet, Link } from 'react-router-dom';
import { authService } from './services/authService';
import { websocketService } from './services/websocketService';
import { I18nProvider, useTranslation } from './i18n';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import HotelNavigation from './components/layout/HotelNavigation';
import HotelProtectedRoute from './components/routing/HotelProtectedRoute';
import AuthGuard from './components/routing/AuthGuard';
import LoginPage from './pages/LoginPage';
import GuestDetailPage from './pages/GuestDetailPage';
import GuestListPage from './pages/GuestListPage';
import MessagesPage from './pages/MessagesPage';
import AlertsPage from './pages/AlertsPage';
import ReportsPage from './pages/ReportsPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import { HotelAdminDashboardPage } from './pages/HotelAdminDashboardPage';
import { StaffManagementPage } from './pages/StaffManagementPage';
import { HotelSettingsPage } from './pages/HotelSettingsPage';
import AnomalyViewPage from './pages/AnomalyViewPage';
import CheckInPage from './pages/CheckInPage';
import AuthDiagnosticPage from './pages/AuthDiagnosticPage';
import RoomTypesPage from './pages/RoomTypesPage';
import RoomsPage from './pages/RoomsPage';
import RatesPage from './pages/RatesPage';
import HotelKYCPage from './pages/HotelKYCPage';
import HotelEnrichedFormPage from './pages/HotelEnrichedFormPage';
import RestaurantsPage from './pages/RestaurantsPage';
import RestaurantFormPage from './pages/RestaurantFormPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForcedPasswordChangePage from './pages/ForcedPasswordChangePage';
import GuestEditPage from './pages/GuestEditPage';
import CheckoutListPage from './pages/CheckoutListPage';
import CheckoutDetailPage from './pages/CheckoutDetailPage';
import PostCheckoutPage from './pages/PostCheckoutPage';
import FPTDeclarationsPage from './pages/FPTDeclarationsPage';
import FPTDeclarationDetailPage from './pages/FPTDeclarationDetailPage';
import FPTDeclarationWizardPage from './pages/FPTDeclarationWizardPage';
import FPTPaymentPage from './pages/FPTPaymentPage';
import FPTUploadProofPage from './pages/FPTUploadProofPage';
import FPTDebitNotesPage from './pages/FPTDebitNotesPage';
import FPTQuittancesPage from './pages/FPTQuittancesPage';
import HotelConnectorsReadOnly from './components/connectors/HotelConnectorsReadOnly';
import CollectorDashboardPage from './pages/CollectorDashboardPage';
import V4DashboardPage from './pages/V4DashboardPage';
import V4DossierPage from './pages/V4DossierPage';
import V4DeclarationsPage from './pages/V4DeclarationsPage';
import V4PaymentsPage from './pages/V4PaymentsPage';
import V4EstablishmentsPage from './pages/V4EstablishmentsPage';
import V4LicensesPage from './pages/V4LicensesPage';
import V4RenewalsPage from './pages/V4RenewalsPage';
import V4QuittancesPage from './pages/V4QuittancesPage';
import V4HistoryPage from './pages/V4HistoryPage';
import V4ReportsPage from './pages/V4ReportsPage';
import V4SettingsPage from './pages/V4SettingsPage';

// Composant Layout avec navigation
function AppLayout() {
  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();

    if (token) {
      try {
        websocketService.connect(token).catch((err: unknown) => {
          if (isMounted) {
            console.error('Error initializing WebSocket:', err);
          }
        });
      } catch (err) {
        if (isMounted) {
          console.error('Error initializing WebSocket:', err);
        }
      }
    }

    // Token refresh interval
    const refreshInterval = setInterval(async () => {
      if (!isMounted) return;
      if (authService.shouldRefreshToken()) {
        try {
          const newToken = await authService.refreshToken();
          if (isMounted && newToken) {
            websocketService.disconnect();
            await websocketService.connect(newToken);
          }
        } catch {
          if (isMounted) {
            authService.logout();
            websocketService.disconnect();
            router.navigate('/login', { replace: true });
          }
        }
      }
    }, 60000);

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
      websocketService.disconnect();
    };
  }, []);

  const handleLogout = () => {
    authService.logout();
    websocketService.disconnect();
    router.navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <HotelNavigation onLogout={handleLogout} />
      <Outlet />
    </div>
  );
}

// Configuration du router avec React Router v7 future flags
const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/force-change-password',
      element: <ForcedPasswordChangePage />,
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: 'guests',
          element: <GuestListPage />,
        },
        {
          path: 'guests/:id',
          element: <GuestDetailPage />,
        },
        {
          path: 'checkin',
          element: <CheckInPage />,
        },
        {
          path: 'auth-diagnostic',
          element: <AuthDiagnosticPage />,
        },
        {
          path: 'anomalies',
          element: <AnomalyViewPage />,
        },
        {
          path: 'messages',
          element: (
            <HotelProtectedRoute permission="MESSAGES_VIEW">
              <MessagesPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'alerts',
          element: (
            <HotelProtectedRoute permission="ALERTS_VIEW">
              <AlertsPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'reports',
          element: (
            <HotelProtectedRoute permission="REPORTS_VIEW">
              <ReportsPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <HotelAdminDashboardPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/staff',
          element: (
            <HotelProtectedRoute permission="STAFF_VIEW">
              <StaffManagementPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/settings',
          element: (
            <HotelProtectedRoute permission="SETTINGS_VIEW">
              <HotelSettingsPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/room-types',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RoomTypesPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/rooms',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RoomsPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/rates',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RatesPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/kyc',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <HotelKYCPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/hotel-enriched',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <HotelEnrichedFormPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/restaurants',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RestaurantsPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/restaurants/new',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RestaurantFormPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'admin/restaurants/:rid/edit',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <RestaurantFormPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'guests/:id/edit',
          element: <GuestEditPage />,
        },
        {
          path: 'change-password',
          element: <ChangePasswordPage />,
        },
        /* Checkout list disabled — individual checkout from guest detail still works */
        {
          path: 'checkout',
          element: <Navigate to="/guests" replace />,
        },
        {
          path: 'checkout/:stayId',
          element: <CheckoutDetailPage />,
        },
        {
          path: 'checkout/:checkoutId/complete',
          element: <PostCheckoutPage />,
        },
        {
          path: 'fpt/v4',
          element: <V4DashboardPage />,
        },
        {
          path: 'fpt/v4/dossier',
          element: <V4DossierPage />,
        },
        {
          path: 'fpt/v4/declarations',
          element: <V4DeclarationsPage />,
        },
        {
          path: 'fpt/v4/payments',
          element: <V4PaymentsPage />,
        },
        {
          path: 'fpt/v4/establishments',
          element: <V4EstablishmentsPage />,
        },
        {
          path: 'fpt/v4/licenses',
          element: <V4LicensesPage />,
        },
        {
          path: 'fpt/v4/renewals',
          element: <V4RenewalsPage />,
        },
        {
          path: 'fpt/v4/quittances',
          element: <V4QuittancesPage />,
        },
        {
          path: 'fpt/v4/history',
          element: <V4HistoryPage />,
        },
        {
          path: 'fpt/v4/reports',
          element: <V4ReportsPage />,
        },
        {
          path: 'fpt/v4/settings',
          element: <V4SettingsPage />,
        },
        {
          path: 'connectors',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <HotelConnectorsReadOnly />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'collector',
          element: (
            <HotelProtectedRoute permission="ADMIN_DASHBOARD">
              <CollectorDashboardPage />
            </HotelProtectedRoute>
          ),
        },
        {
          path: 'access-denied',
          element: <AccessDeniedPage />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/login" replace />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  } as any // Type cast pour supporter les future flags v7
);

function App() {
  useEffect(() => {
    // Token refresh interval — only runs if authenticated
    let refreshInterval: NodeJS.Timeout | null = null;
    let isMounted = true;

    if (authService.isAuthenticated()) {
      refreshInterval = setInterval(async () => {
        if (!isMounted) return;

        if (authService.shouldRefreshToken()) {
          try {
            await authService.refreshToken();
          } catch (error) {
            console.error('Token refresh failed:', error);
            if (isMounted) {
              authService.logout();
              router.navigate('/login', { replace: true });
            }
          }
        }
      }, 60000);
    }

    return () => {
      isMounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  return (
    <I18nProvider>
      <SubscriptionProvider>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </SubscriptionProvider>
    </I18nProvider>
  );
}

/**
 * HomePage Component
 * 
 * IMPORTANT PATTERN: React Router Navigation
 * 
 * This component demonstrates correct navigation using React Router's <Link> component.
 * 
 * ✅ CORRECT: Using <Link to="/path">
 * - No page reload
 * - Preserves application state
 * - Proper component lifecycle management
 * - No DOM manipulation errors
 * 
 * ❌ WRONG: Using <a href="/path">
 * - Causes full page reload
 * - Loses application state
 * - Can cause "removeChild" DOM errors
 * - Poor user experience
 * 
 * Rule: ALWAYS use <Link> for internal navigation, NEVER use <a href>
 * 
 * See: REACT_LIFECYCLE_GUIDE.md - Navigation Best Practices
 */
function HomePage() {
  const { t } = useTranslation();
  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('home.welcome')}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('app.subtitle')}
          </p>
          <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold mb-4">{t('home.quickActions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/checkin"
                className="p-4 border-2 border-purple-500 bg-purple-50 rounded-lg hover:bg-purple-100 transition md:col-span-2"
              >
                <div className="text-purple-600 mb-2">
                  <svg className="w-10 h-10 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h4 className="font-semibold text-purple-700 text-lg">{t('home.newPoliceFile')}</h4>
                <p className="text-sm text-purple-500 mt-1">{t('home.newPoliceFileDesc')}</p>
              </Link>
              <Link
                to="/guests"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="text-blue-600 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="font-medium">{t('home.policeFiles')}</h4>
                <p className="text-sm text-gray-500 mt-1">{t('home.policeFilesDesc')}</p>
              </Link>
              <Link
                to="/messages"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="text-green-600 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h4 className="font-medium">{t('home.messages')}</h4>
                <p className="text-sm text-gray-500 mt-1">{t('home.messagesDesc')}</p>
              </Link>
              <Link
                to="/reports"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="text-orange-600 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="font-medium">{t('home.reports')}</h4>
                <p className="text-sm text-gray-500 mt-1">{t('home.reportsDesc')}</p>
              </Link>
              <Link
                to="/anomalies"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="text-yellow-600 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h4 className="font-medium">{t('home.anomalies')}</h4>
                <p className="text-sm text-gray-500 mt-1">{t('home.anomaliesDesc')}</p>
              </Link>
              <div
                className="p-4 border border-gray-200 rounded-lg opacity-40 cursor-not-allowed"
                title="Fonctionnalité désactivée"
              >
                <div className="text-gray-400 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <h4 className="font-medium text-gray-400">Check-out</h4>
                <p className="text-sm text-gray-400 mt-1">Clôturer les séjours</p>
              </div>
              <div
                className="p-4 border border-gray-200 rounded-lg opacity-40 cursor-not-allowed"
                title="Fonctionnalité désactivée"
              >
                <div className="text-gray-400 mb-2">
                  <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                </div>
                <h4 className="font-medium text-gray-400">Déclarations FPT</h4>
                <p className="text-sm text-gray-400 mt-1">Taxes touristiques</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
